import React from 'react';
import { Inbox, UserCheck, ClipboardCheck, ShieldCheck, Send, CheckCircle } from 'lucide-react';
const stages = [{
  icon: Inbox,
  title: 'Instruction Receipt',
  description: 'Secure, GDPR-compliant portal for case documentation. Your instruction is logged and acknowledged within 24 hours (mark "Urgent" for priority response).',
  status: 'Logged & secured'
}, {
  icon: UserCheck,
  title: 'Expert Allocation',
  description: 'Matching cases to the most appropriate GMC-registered specialist based on discipline, location, and availability.',
  status: 'Specialist matched'
}, {
  icon: ClipboardCheck,
  title: 'Clinical Triage',
  description: 'Internal review ensuring all legal questions are addressed. Case records assessed and examination arranged.',
  status: 'Triage complete'
}, {
  icon: ShieldCheck,
  title: 'Quality Assurance',
  description: 'Final audit of report clarity and CPR Part 35 compliance. Every report undergoes rigorous clinical governance review.',
  status: 'Peer-reviewed'
}, {
  icon: Send,
  title: 'Secure Delivery',
  description: 'Encrypted transfer of the final, court-ready evidence. Delivered securely within the agreed timescale.',
  status: 'Delivered to you'
}];
export function InstructionTracker() {
  return <section className="py-10 bg-warm-100" aria-label="Infographic detailing the 5-step clinical governance process for medical legal reporting" data-id="element-270">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-271">
        <div className="text-center max-w-2xl mx-auto mb-8" data-id="element-272">
          <div className="inline-flex items-center bg-teal-50 text-teal-700 text-sm font-semibold px-4 py-2 rounded-full mb-5 border border-teal-100" data-id="element-273">
            <ShieldCheck className="h-4 w-4 mr-2" data-id="element-274" />
            Clinical Governance Process
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-ink-900 tracking-tight mb-4" data-id="element-275">
            Your Instruction Never Gets Lost
          </h2>
          <p className="text-lg text-ink-500 leading-relaxed" data-id="element-276">
            Every instruction enters a rigorous, multi-stage clinical governance
            pipeline. Full transparency from receipt to delivery — no black
            boxes.
          </p>
        </div>

        {/* Desktop: horizontal stage bar */}
        <div className="hidden lg:block" data-id="element-277">
          <div className="relative" data-id="element-278">
            {/* Progress bar background */}
            <div className="absolute top-[2.75rem] left-[10%] right-[10%] h-1 bg-warm-200 rounded-full z-0" data-id="element-279" />
            {/* Progress bar filled */}
            <div className="absolute top-[2.75rem] left-[10%] right-[10%] h-1 bg-gradient-to-r from-teal-500 via-teal-400 to-teal-500 rounded-full z-0" data-id="element-280" />

            <div className="relative z-10 grid grid-cols-5 gap-4" data-id="element-281">
              {stages.map((stage, index) => <div key={stage.title} className="flex flex-col items-center text-center" data-id="element-282">
                  {/* Stage circle */}
                  <div className="relative mb-6" data-id="element-283">
                    <div className="w-[5.5rem] h-[5.5rem] rounded-full bg-white border-[3px] border-teal-500 flex items-center justify-center shadow-md" data-id="element-284">
                      <stage.icon className="h-7 w-7 text-teal-600" data-id="element-285" />
                    </div>
                    {/* Stage number */}
                    <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-cta-500 text-white flex items-center justify-center text-xs font-bold shadow-sm" data-id="element-286">
                      {index + 1}
                    </div>
                    {/* Checkmark */}
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-sage-500 text-white flex items-center justify-center shadow-sm" data-id="element-287">
                      <CheckCircle className="h-4 w-4" data-id="element-288" />
                    </div>
                  </div>

                  <h3 className="text-base font-bold text-ink-900 mb-2 leading-tight" data-id="element-289">
                    {stage.title}
                  </h3>
                  <p className="text-sm text-ink-500 leading-relaxed mb-3 max-w-[200px]" data-id="element-290">
                    {stage.description}
                  </p>
                  <span className="inline-flex items-center text-xs font-semibold text-sage-600 bg-sage-50 px-3 py-1 rounded-full border border-sage-100" data-id="element-291">
                    <CheckCircle className="h-3 w-3 mr-1.5" data-id="element-292" />
                    {stage.status}
                  </span>
                </div>)}
            </div>
          </div>
        </div>

        {/* Tablet: 2-column + 1 */}
        <div className="hidden md:block lg:hidden" data-id="element-293">
          <div className="space-y-4" data-id="element-294">
            {stages.map((stage, index) => <div key={stage.title} className="flex items-start gap-5 bg-white rounded-2xl border border-warm-200 p-6 shadow-sm" data-id="element-295">
                <div className="flex flex-col items-center flex-shrink-0" data-id="element-296">
                  <div className="w-14 h-14 rounded-full bg-teal-50 border-2 border-teal-500 flex items-center justify-center relative" data-id="element-297">
                    <stage.icon className="h-6 w-6 text-teal-600" data-id="element-298" />
                    <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-cta-500 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-299">
                      {index + 1}
                    </div>
                  </div>
                  {index < stages.length - 1 && <div className="w-0.5 h-4 bg-teal-200 mt-2" data-id="element-300" />}
                </div>
                <div className="flex-1 pt-1" data-id="element-301">
                  <div className="flex items-center justify-between mb-2" data-id="element-302">
                    <h3 className="text-lg font-bold text-ink-900" data-id="element-303">
                      {stage.title}
                    </h3>
                    <span className="inline-flex items-center text-xs font-semibold text-sage-600 bg-sage-50 px-2.5 py-1 rounded-full border border-sage-100" data-id="element-304">
                      <CheckCircle className="h-3 w-3 mr-1" data-id="element-305" />
                      {stage.status}
                    </span>
                  </div>
                  <p className="text-sm text-ink-500 leading-relaxed" data-id="element-306">
                    {stage.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Mobile: vertical timeline */}
        <div className="md:hidden" data-id="element-307">
          <div className="space-y-0" data-id="element-308">
            {stages.map((stage, index) => <div key={stage.title} className="flex items-start gap-4" data-id="element-309">
                {/* Left: line + circle */}
                <div className="flex flex-col items-center flex-shrink-0" data-id="element-310">
                  <div className="w-12 h-12 rounded-full bg-white border-2 border-teal-500 flex items-center justify-center shadow-sm relative" data-id="element-311">
                    <stage.icon className="h-5 w-5 text-teal-600" data-id="element-312" />
                    <div className="absolute -top-1 -right-1 w-5.5 h-5.5 rounded-full bg-cta-500 text-white flex items-center justify-center text-[9px] font-bold" style={{
                  width: '22px',
                  height: '22px'
                }} data-id="element-313">
                      {index + 1}
                    </div>
                  </div>
                  {index < stages.length - 1 && <div className="w-0.5 h-6 bg-teal-200 my-1" data-id="element-314" />}
                </div>

                {/* Right: content */}
                <div className="pt-1.5 pb-6" data-id="element-315">
                  <h3 className="text-base font-bold text-ink-900 mb-1" data-id="element-316">
                    {stage.title}
                  </h3>
                  <p className="text-sm text-ink-500 leading-relaxed mb-2" data-id="element-317">
                    {stage.description}
                  </p>
                  <span className="inline-flex items-center text-xs font-semibold text-sage-600 bg-sage-50 px-2.5 py-1 rounded-full border border-sage-100" data-id="element-318">
                    <CheckCircle className="h-3 w-3 mr-1" data-id="element-319" />
                    {stage.status}
                  </span>
                </div>
              </div>)}
          </div>
        </div>

        {/* Bottom reassurance */}
        <div className="mt-14 text-center" data-id="element-320">
          <div className="inline-flex items-center bg-white rounded-2xl border border-warm-200 px-8 py-5 shadow-sm" data-id="element-321">
            <ShieldCheck className="h-6 w-6 text-teal-600 mr-4 flex-shrink-0" data-id="element-322" />
            <div className="text-left" data-id="element-323">
              <p className="text-sm font-bold text-ink-900" data-id="element-324">
                Every report is peer-reviewed
              </p>
              <p className="text-xs text-ink-500" data-id="element-325">
                Clinical governance checks ensure CPR Part 35 compliance,
                accuracy, and completeness before delivery.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}